import { Component, OnInit, OnDestroy, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { OrderService } from '../../../order.service';
import { Html5Qrcode, Html5QrcodeSupportedFormats } from 'html5-qrcode';

@Component({
  selector: 'app-add-gift-card-dialog',
  templateUrl: './add-gift-card-dialog.component.html',
  styleUrls: ['./add-gift-card-dialog.component.sass'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AddGiftCardDialogComponent implements OnInit, OnDestroy {
  form: FormGroup;
  isLoading: boolean = false;
  error: string = '';
  storeId: string;
  phoneNumber: string;

  // Scanner properties
  scannerEnabled: boolean = false;
  private html5Qrcode: Html5Qrcode = null;
  isScannerStarting: boolean = false;
  private hasScanned: boolean = false;
  private isDestroyed: boolean = false;
  // A single decoded frame can be a partial read, so a value must be seen
  // twice before it is accepted.
  private lastCandidate: string = '';
  private candidateCount: number = 0;

  constructor(
    private dialogRef: MatDialogRef<AddGiftCardDialogComponent>,
    private fb: FormBuilder,
    private orderService: OrderService,
    private snackBar: MatSnackBar,
    private cdr: ChangeDetectorRef
  ) {
    this.storeId = localStorage.getItem('currentStore');
    this.phoneNumber = localStorage.getItem('phoneNumber');
  }

  ngOnInit() {
    this.form = this.fb.group({
      cardNumber: ['', [Validators.required]]
    });
  }

  ngOnDestroy() {
    this.isDestroyed = true;
    this.stopScanner();
  }

  private refresh() {
    if (!this.isDestroyed) {
      this.cdr.markForCheck();
    }
  }

  async startScanner() {
    this.error = '';
    this.hasScanned = false;
    this.lastCandidate = '';
    this.candidateCount = 0;
    this.isScannerStarting = true;
    this.scannerEnabled = true;
    this.refresh();

    // Wait for the DOM element to be available
    setTimeout(async () => {
      try {
        this.html5Qrcode = new Html5Qrcode('scanner-container');

        const config = {
          fps: 15,
          qrbox: { width: 280, height: 160 },
          aspectRatio: 1.333,
          disableFlip: true,
          // Use the enum, not raw numbers — the numeric values are not in the
          // order you'd guess (CODE_128 is 5, not 4).
          formatsToSupport: [
            Html5QrcodeSupportedFormats.CODE_128,
            Html5QrcodeSupportedFormats.EAN_13,
            Html5QrcodeSupportedFormats.EAN_8,
            Html5QrcodeSupportedFormats.UPC_A,
            Html5QrcodeSupportedFormats.UPC_E,
          ]
        };

        await this.html5Qrcode.start(
          { facingMode: 'environment' },
          config,
          (decodedText) => {
            this.onScanSuccess(decodedText);
          },
          (errorMessage) => {
            // Ignore scan errors (they happen frequently when no barcode is in view)
          }
        );

        this.isScannerStarting = false;
        this.refresh();
      } catch (err) {
        this.isScannerStarting = false;
        this.scannerEnabled = false;
        this.error = 'Unable to access camera. Please check permissions or enter card number manually.';
        this.refresh();
      }
    }, 100);
  }

  async stopScanner() {
    if (this.html5Qrcode) {
      try {
        await this.html5Qrcode.stop();
        this.html5Qrcode.clear();
      } catch (err) {
      }
      this.html5Qrcode = null;
    }
    this.scannerEnabled = false;
    this.isScannerStarting = false;
    this.refresh();
  }

  // Minimum plausible gift card number length; shorter reads are partial.
  private static readonly MIN_CARD_LENGTH = 8;
  // How many identical reads are needed before a value is trusted.
  private static readonly REQUIRED_MATCHES = 3;

  async onScanSuccess(result: string) {
    // The scan callback fires on every decoded frame; ignore repeats once handled.
    if (!result || this.hasScanned) {
      return;
    }

    const candidate = result.trim();

    // A truncated read is worse than no read at all — it would link the wrong
    // card. Discard anything too short to be a real card number.
    if (candidate.length < AddGiftCardDialogComponent.MIN_CARD_LENGTH) {
      return;
    }

    // Require the same value on several consecutive decodes. At 15fps this
    // costs about 200ms on a clean scan, but a partial read almost never
    // repeats identically three times.
    if (candidate === this.lastCandidate) {
      this.candidateCount++;
    } else {
      this.lastCandidate = candidate;
      this.candidateCount = 1;
    }

    if (this.candidateCount < AddGiftCardDialogComponent.REQUIRED_MATCHES) {
      return;
    }

    this.hasScanned = true;
    await this.stopScanner();

    this.form.patchValue({ cardNumber: candidate });
    this.form.get('cardNumber').markAsDirty();
    this.snackBar.open('Card number scanned successfully!', '', {
      duration: 2000,
      verticalPosition: 'top'
    });
    this.refresh();
  }

  submit() {
    if (this.form.valid && !this.isLoading) {
      this.isLoading = true;
      this.error = '';

      const cardNumber = this.form.get('cardNumber').value;

      this.orderService.linkGiftCard(cardNumber, this.phoneNumber, this.storeId).subscribe(
        (response) => {
          this.isLoading = false;
          this.snackBar.open('Gift card added successfully', '', {
            duration: 3000,
            verticalPosition: 'top'
          });
          this.refresh();
          this.dialogRef.close(response);
        },
        (error) => {
          this.isLoading = false;
          // Extract error message from various possible backend response formats
          if (error.error) {
            if (typeof error.error === 'string') {
              this.error = error.error;
            } else if (error.error.message) {
              this.error = error.error.message;
            } else if (error.error.detail) {
              this.error = error.error.detail;
            } else if (error.error.error) {
              this.error = error.error.error;
            } else if (error.error.non_field_errors) {
              this.error = error.error.non_field_errors.join(', ');
            } else if (error.error.card_number) {
              this.error = error.error.card_number.join(', ');
            } else {
              this.error = 'Failed to link gift card. Please try again.';
            }
          } else {
            this.error = error.message || 'Failed to link gift card. Please try again.';
          }
          this.refresh();
        }
      );
    }
  }

  dismiss() {
    this.stopScanner();
    this.dialogRef.close();
  }
}
